import { pipe } from 'remeda';
import * as R from 'remeda';
import { z } from 'zod';

import { sessionConfigSchema } from '../../forms/ExperienceForm/formSchema';
import { StepType } from '../../types';

export type SessionConfig = z.infer<typeof sessionConfigSchema>;
export type TransformedStep =
  | { question: string; answers: string[]; condition: string }
  | { video: string; audio?: string; condition: string };

/**
 * Trasforma i dati del form di esperienza nel formato richiesto.
 */
export function transformExperienceFormData(
  formData: SessionConfig
): TransformedStep[] {
  const stepConditions = pipe(formData.steps, steps =>
    pipe(
      steps,
      s => s.map((step, idx) => ({ step, idx })),
      stepEntries =>
        R.reduce(
          stepEntries,
          (acc, { step, idx }) => {
            if (step.type !== StepType.QUESTION) {
              return acc;
            }

            return R.reduce(
              step.answers ?? [],
              (innerAcc, answer) => {
                const targetStepId = answer?.goToStepId;

                if (targetStepId && answer.label && !innerAcc[targetStepId]) {
                  return { ...innerAcc, [targetStepId]: answer.label };
                }

                if (!targetStepId && answer.label) {
                  const nextStep = pipe(
                    steps,
                    R.drop(idx + 1),
                    R.find(candidate => candidate.type !== StepType.QUESTION)
                  );

                  if (nextStep && !innerAcc[nextStep.id]) {
                    return { ...innerAcc, [nextStep.id]: answer.label };
                  }
                }

                return innerAcc;
              },
              acc
            );
          },
          {} as Record<string, string>
        )
    )
  );

  return pipe(
    formData.steps,
    R.map(step => {
      const condition = stepConditions[step.id] ?? '';

      if (step.type === StepType.QUESTION) {
        return {
          question: step.text,
          answers: pipe(
            step.answers ?? [],
            R.map(answer => answer.label)
          ),
          condition,
        };
      }

      if (step.type === StepType.VIDEO) {
        const videoId = step.videoId ?? step.audioId ?? '';

        return {
          video: videoId,
          ...(step.audioId && { audio: step.audioId }),
          condition,
        };
      }

      throw new Error(`Tipo di step non supportato: ${JSON.stringify(step)}`);
    })
  );
}
